import { React, useEffect, useState } from "react";
import KeyboardArrowRightIcon from "@mui/icons-material/ArrowForwardIos";
import MDTypography from "components/MDTypography";
import { TextField } from "@mui/material";
import PropTypes from "prop-types";
import griskApiData from "./tableData";

// Build the sub-section links (Request Endpoint, Request Headers, Request Body, Response Body)
// for a single endpoint section using the anchors that already exist in the renderer.
// Filter: include a bodyBlock as a nav item if it has a heading and is not a pure
// example/description block. Excluded headings are those known to be non-navigable.
const NAV_EXCLUDE_HEADINGS = new Set([
  "Column Descriptions",
  "Pre Trade Policies Table",
  "Table 2 – Nested data Object Fields",
]);
const shouldIncludeBlock = (blk) =>
  blk.heading &&
  !blk.heading.toLowerCase().includes("example") &&
  !NAV_EXCLUDE_HEADINGS.has(blk.heading);

const buildEndpointChildren = (sec) => {
  const kids = [];
  if (sec.endpointId && sec.endPointHeading) {
    kids.push({ id: sec.endpointId, label: sec.endPointHeading });
  }
  if (sec.requestHeader && sec.requestHeaderId) {
    kids.push({ id: sec.requestHeaderId, label: sec.requestHeader });
  }

  if (sec.bodyBlocks) {
    // Collect lead blocks (appear before the main request grid)
    const leadItems = sec.bodyBlocks
      .flatMap((blk, bi) => (blk.zone === "lead" && shouldIncludeBlock(blk)) ? [{ id: `${sec.mainGridId}-blk-${bi}`, label: blk.heading }] : []);
    // Collect main + rightcode blocks (appear inside the request grid)
    const mainItems = sec.bodyBlocks
      .flatMap((blk, bi) => (blk.zone !== "lead" && shouldIncludeBlock(blk)) ? [{ id: `${sec.mainGridId}-blk-${bi}`, label: blk.heading }] : []);

    // Add requestObject as "Request Body" between lead and main blocks,
    // but only when no bodyBlock already uses that label (avoids duplicates).
    const hasBodyLabel = sec.bodyBlocks.some(b => b.heading === "Request Body");
    const hasRightcodeHeading = sec.bodyBlocks.some(b => b.zone === "rightcode" && b.heading);
    const insertRequestObject =
      sec.requestObject && !hasBodyLabel && !hasRightcodeHeading;

    kids.push(...leadItems);
    if (insertRequestObject) {
      kids.push({ id: `${sec.mainGridId}-request-object`, label: "Request Body" });
    }
    kids.push(...mainItems);
  } else if (sec.requestBody && sec.requestBodyId) {
    kids.push({ id: sec.requestBodyId, label: sec.requestBody });
  }

  if (sec.responseTables) {
    const responseKids = [];
    sec.responseTables.forEach((rt, ri) => {
      if (rt.status) {
        responseKids.push({ id: `${sec.mainGridId}-response-${ri}`, label: rt.status, noHighlight: true });
      }
    });
    kids.push({
      id: `${sec.mainGridId}-response`,
      label: "Response Body",
      children: responseKids.length > 0 ? responseKids : undefined,
    });
  } else if (sec.respObjKey) {
    kids.push({ id: `${sec.mainGridId}-response`, label: "Response Body" });
  }
  return kids;
};

// Build the full nav tree from the documentation data.
const buildNavTree = (data) => {
  const items = [];
  data.forEach((sec) => {
    if (sec.navLabel) {
      // Entity anchor (Risk Profile, Margin Profile, etc.) — not shown in Confluence,
      // so it is skipped in the nav; its endpoints appear directly.
      return;
    }
    if (sec.mainGridIdStatic) {
      items.push({ id: sec.mainGridIdStatic, label: sec.mainHeadingStatic });
    } else if (sec.mainHeading) {
      items.push({
        id: sec.mainGridId,
        label: sec.mainHeading,
        children: buildEndpointChildren(sec),
      });
    }
  });
  return items;
};

// Build a flat ordered list of { id, targets } for scroll tracking (recursive).
const buildScrollSections = (items) => {
  const sections = [];
  const flatten = (list) => {
    list.forEach((item) => {
      if (!item.noHighlight) {
        sections.push({ id: item.id, targets: [item.id] });
      }
      if (item.children) flatten(item.children);
    });
  };
  flatten(items);
  return sections;
};

function MultiSelectTreeView(props) {
  const [searchTerm, setSearchTerm] = useState("");
  const [expanded, setExpanded] = useState({});
  const [activeNavId, setActiveNavId] = useState("");

  const toggle = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    props.searchedData(e.target.value);
  };

  const navItems = buildNavTree(griskApiData);
  const scrollSections = buildScrollSections(navItems);

  useEffect(() => {
    const scrollContainer = props.scrollRef?.current;
    if (!scrollContainer) {
      return undefined;
    }

    const syncActiveSection = () => {
      const containerTop = scrollContainer.getBoundingClientRect().top;
      let nextActiveId = scrollSections[0]?.id ?? "";

      if (scrollContainer.scrollTop <= 0) {
        setActiveNavId((cur) => (cur === nextActiveId ? cur : nextActiveId));
        return;
      }

      scrollSections.forEach(({ id, targets }) => {
        const el = targets.map((t) => document.getElementById(t)).find(Boolean);
        if (!el) return;
        const top = el.getBoundingClientRect().top - containerTop;
        if (top <= 140) nextActiveId = id;
      });

      setActiveNavId((cur) => (cur === nextActiveId ? cur : nextActiveId));
    };

    syncActiveSection();
    scrollContainer.addEventListener("scroll", syncActiveSection, { passive: true });
    return () => scrollContainer.removeEventListener("scroll", syncActiveSection);
  }, [props.scrollRef, scrollSections]);

  const activeNavClass = (id) => (activeNavId === id ? "ss-actives" : "");

  const renderItem = (item) => {
    if (item.children && item.children.length) {
      const isOpen = !!expanded[item.id];
      return (
        <li key={item.id}>
          <span
            className={`caret${isOpen ? " caret-down" : ""}`}
            onClick={() => toggle(item.id)}
          >
            <a href={`#${item.id}`} onClick={() => setActiveNavId(item.id)}>
              <MDTypography
                variant="h6"
                fontSize="13px"
                fontWeight="regular"
                fontFamily="inherit"
              >
                <div className={activeNavClass(item.id)} data-to-scrollspy-id={item.id}>
                  {item.label}
                  <KeyboardArrowRightIcon />
                </div>
              </MDTypography>
            </a>
          </span>
          <ul className={`nested${isOpen ? " active" : ""}`}>
            {item.children.map(renderItem)}
          </ul>
        </li>
      );
    }
    return (
      <li key={item.id}>
        <a href={`#${item.id}`} onClick={() => setActiveNavId(item.id)}>
          <MDTypography
            variant="h6"
            fontSize="13px"
            fontWeight="regular"
            fontFamily="inherit"
          >
            <div
              className={activeNavClass(item.id)}
              data-to-scrollspy-id={item.id}
            >
              {item.label}
            </div>
          </MDTypography>
        </a>
      </li>
    );
  };

  return (
    <>
      <TextField
        className="outlined-basic-subject search-doc my-2"
        placeholder="Search docs..."
        value={searchTerm}
        onChange={handleInputChange}
        autoComplete="off"
      />
      {props.titleNode}
      <MDTypography
        variant="p"
        fontSize="13px"
        fontWeight="regular"
        fontFamily="inherit"
        color="white"
      >
        <ul className="docs-nav">{navItems.map(renderItem)}</ul>
      </MDTypography>
    </>
  );
}
MultiSelectTreeView.propTypes = {
  searchedData: PropTypes.func.isRequired,
  scrollRef: PropTypes.shape({
    current: PropTypes.instanceOf(Element),
  }).isRequired,
};
export default MultiSelectTreeView;
